import { useEffect, useState, useCallback } from "react";
import { KeyRound } from "lucide-react";
import api from "@/lib/api";

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const OUTCOME = {
  success: { label: "Signed in", cls: "bg-green-100 text-green-700" },
  failed_password: { label: "Wrong password", cls: "bg-red-100 text-red-700" },
  failed_2fa: { label: "Wrong 2FA code", cls: "bg-red-100 text-red-700" },
  "2fa_required": { label: "Awaiting 2FA", cls: "bg-amber-100 text-amber-700" },
};

export function LoginAuditCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/auth/login-audit", { params: { limit: 100 } })
      .then(({ data }) => setRows(data))
      .catch((err) => console.error("Failed to load login audit:", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const failures = rows.filter((r) => r.outcome.startsWith("failed")).length;

  return (
    <div className="bg-white border border-[#E5E5E5] p-8" data-testid="login-audit-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 gv-label">
          <KeyRound className="w-4 h-4" /> Sign-in attempts
        </div>
        <button type="button" onClick={load} className="text-xs tracking-[0.2em] uppercase text-[#5C6670] hover:text-[#C5A059]" data-testid="login-audit-refresh">
          Refresh
        </button>
      </div>
      <p className="text-sm text-[#5C6670] mt-2 mb-6">
        Every attempt to enter the studio — successful sign-ins and failed / intrusion attempts, with the IP and device used.
        {failures > 0 && <span className="text-red-600 font-medium"> {failures} failed attempt{failures !== 1 ? "s" : ""} recently.</span>}
      </p>

      {loading ? (
        <p className="text-sm text-[#B8BCC2]">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[#5C6670] italic" data-testid="login-audit-empty">No sign-in attempts recorded yet.</p>
      ) : (
        <ul className="divide-y divide-[#F0F0F0] max-h-96 overflow-y-auto" data-testid="login-audit-list">
          {rows.map((r, i) => {
            const o = OUTCOME[r.outcome] || { label: r.outcome, cls: "bg-gray-100 text-gray-600" };
            return (
              <li key={`${r.created_at}-${i}`} className="py-3 flex items-start justify-between gap-3 text-xs" data-testid={`login-audit-row-${i}`}>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className={`text-[0.6rem] tracking-[0.14em] uppercase px-1.5 py-0.5 rounded ${o.cls}`} data-testid={`login-audit-outcome-${i}`}>
                      {o.label}
                    </span>
                    <span className="font-mono text-[#1A1F24]" data-testid={`login-audit-ip-${i}`}>{r.ip}</span>
                  </div>
                  <div className="text-[#5C6670]">{r.email}</div>
                  {r.user_agent && <div className="text-[#9C9C94] break-all">{r.user_agent}</div>}
                </div>
                <span className="text-[#B8BCC2] whitespace-nowrap shrink-0">{fmt(r.created_at)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
